import React, { useEffect, useState } from 'react';
import CircleButton, {
  getCircleButtonClass,
} from 'components/v2/buttons/CircleButton';
import { DocumentCheckIcon } from '@heroicons/react/24/solid';
import CheckIcon from 'components/v2/icons/CheckIcon';
import CargomaticTooltip from 'components/v2/Tooltip';
import { useTranslation } from 'react-i18next';
import StopCompleteModal from 'components/modules/shipments/stops/StopCompleteModal';
import SessionStore from 'stores/SessionStore';
import { getYards } from 'utils/YardAPIUtils';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import ShipmentWebAPIUtils, {
  handleStopEquipmentUpdateFail,
  handleStopEquipmentUpdateSuccess,
} from 'utils/ShipmentWebAPIUtils';
import OutgateConfirmationModal from 'components/modules/shipments/OutgateConfirmationModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import {
  isDrayageShipment,
  isIntermodalShipmentType,
} from 'helpers/shipment-types';
import { getShipperStopDocumentRequirementsFromConfig } from 'helpers/stops';

const initConfirmationModalDetails = {
  type: 'outgate',
  title: 'ADDITIONAL_STOP_CONFIRM_TITLE',
  message: 'ADDITIONAL_STOP_CONFIRM_MESSAGE',
  confirmButtonText: 'YES',
  hideButtonText: 'NO',
};

export default function StopCompleteNextStep({ item, stops, shipment }) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [yards, setYards] = useState(null);
  const [confirmationModalDetails, setConfirmationModalDetails] = useState(
    initConfirmationModalDetails,
  );
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showOutgateConfirmationModal, setShowOutgateConfirmationModal] =
    useState(false);
  const [shipperStopDocumentRequirements, setShipperStopDocumentRequirements] =
    useState({});

  const isCarrier = SessionStore.isCarrier();

  useEffect(() => {
    const getYardsData = async () => {
      const {
        market: { market_id: marketId },
      } = shipment;
      const yardsResponse = await getYards(
        'nameInternal',
        'ascend',
        null,
        null,
        {
          id: null,
          marketId,
          nameInternal: null,
          status: null,
        },
      );
      setYards(yardsResponse?.data?.getYards?.yards);
    };

    const getShipperStopDocRequirements = async () => {
      if (isIntermodalShipmentType) {
        const shipperId = shipment?.shipper?._id || shipment?.shipper;
        const stopDocReqs =
          await getShipperStopDocumentRequirementsFromConfig(shipperId);
        setShipperStopDocumentRequirements(stopDocReqs);
      }
    };
    try {
      getYardsData();
      getShipperStopDocRequirements();
    } catch (e) {
      console.error('Error Loading Next Steps', e);
      setShipperStopDocumentRequirements({});
    }
  }, [shipment]);

  const createAction = () => {
    const stop = stops.find((s) => s._id === item?.meta?.stop);
    const offsite = stop?.off_site ? '_OFFSITE' : '';
    const empty = stop?.empty ? '_EMPTY' : '';
    const action = stop?.action === 'deliver' ? '_DELIVERY' : '_PICKUP';
    return `COMPLETE${offsite}${empty}${action}`;
  };

  const renderIcon = () => {
    return !item.complete ? (
      <DocumentCheckIcon type="solid" className="w-5 h-5" />
    ) : (
      <div
        data-testid="div-d98ab4f0"
        className={getCircleButtonClass({ size: 'small', color: 'brand' })}>
        <CheckIcon />
      </div>
    );
  };

  const handleStopCompletion = (shipment, currentStop, file, meta) => {
    const {
      shipper: { prompt_for_offsite, tags },
    } = shipment;
    const promptForOutgate =
      prompt_for_offsite || (tags || []).includes('prompt_for_offsite');
    ShipmentsActionCreators.completeStop(shipment, currentStop, file, meta);
    if (promptForOutgate) {
      handleOutgateConfirmationDetails(currentStop);
    }
  };

  const handleOutgateConfirmationDetails = (stop) => {
    if (isDrayageShipment(shipment) && stop.action === 'pickup') {
      setConfirmationModalDetails({
        ...confirmationModalDetails,
        stop,
      });
      setShowConfirmationModal(true);
    }
  };

  const handleOutgateConfirmation = () => {
    if (confirmationModalDetails.type === 'outgate') {
      setShowConfirmationModal(false);
      setShowOutgateConfirmationModal(true);
    }
  };

  const handleOutgateStopCreation = async (payload) => {
    setShowOutgateConfirmationModal(false);
    await ShipmentWebAPIUtils.createOutgateShipmentStops(shipment._id, payload);
  };

  const renderCompleteButton = (item, stops) => {
    if (!(item && stops)) {
      return null;
    }

    const circleButton = (shouldDisable) => (
      <CircleButton
        dataTestId="CircleButton-e48d9b8a"
        size="small"
        disabled={shouldDisable}
        onClick={() => setShowModal(true)}>
        {renderIcon()}
      </CircleButton>
    );

    if (item.complete) {
      return circleButton(true);
    }

    const disabled = !!item.disabled;
    if (disabled) {
      return (
        (<CargomaticTooltip
          dataTestId="CargomaticTooltip-c21c5cc7"
          position="top"
          body={() => circleButton(disabled)}>
          {t('COMPLETE_PREVIOUS_STEPS')}
        </CargomaticTooltip>)
      );
    }
    return circleButton(false);
  };

  return (
    (<div data-testid="div-37d824a6">
      <div
        data-testid="div-67ad8093"
        className="flex justify-between text-sm text-gray-700  py-1 items-center">
        <span data-testid="span-75bb09f0">{t(createAction())}</span>
        {renderCompleteButton(item, stops)}
      </div>
      {showModal && (
        <StopCompleteModal
          show={showModal}
          shipmentId={shipment._id}
          stopId={item?.meta?.stop}
          onStopComplete={handleStopCompletion}
          onStopEquipmentUpdateSuccess={handleStopEquipmentUpdateSuccess}
          onStopEquipmentUpdateFail={handleStopEquipmentUpdateFail}
          onHide={() => setShowModal(false)}
          isCarrier={isCarrier}
          yards={yards}
          shipperStopDocumentRequirements={shipperStopDocumentRequirements}
        />
      )}
      {showConfirmationModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-fc330225"
          show={showConfirmationModal}
          title={t(confirmationModalDetails.title)}
          message={t(confirmationModalDetails.message)}
          affirmative={t(confirmationModalDetails.confirmButtonText)}
          hideButtonText={t(confirmationModalDetails.hideButtonText)}
          onConfirm={handleOutgateConfirmation}
          onHide={() => setShowConfirmationModal(false)} />
      )}
      {showOutgateConfirmationModal && (
        <OutgateConfirmationModal
          show={showOutgateConfirmationModal}
          onCancel={() => setShowOutgateConfirmationModal(false)}
          onSave={handleOutgateStopCreation}
          stop={confirmationModalDetails.stop}
        />
      )}
    </div>)
  );
}
